import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FiArrowUpRight, FiCalendar, FiUser, FiSearch } from 'react-icons/fi';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const cats = ['All', ...new Set(posts.map(p => p.category))];
    return cats;
  }, [posts]);

  const filteredPosts = useMemo(() => {
    let result = posts;
    if (activeCategory !== 'All') {
      result = result.filter(p => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.author.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    }
    return result;
  }, [posts, activeCategory, searchQuery]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="bg-black text-white" data-testid="blog-page">
      <SEO title="Blog" description="Industry insights, thought leadership, and updates from across Arvaron's business verticals." url="/blog" />
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#0a0a0a] to-black" />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-6">Blog</p>
            <h1 className="font-display text-5xl md:text-7xl font-bold mb-6" data-testid="blog-title">
              Insights & <span className="brand-gradient-text">Perspectives</span>
            </h1>
            <p className="text-lg text-white/50 max-w-3xl mx-auto">
              Industry insights, thought leadership, and updates from across our business verticals
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Search & Filter Bar */}
          {!loading && posts.length > 0 && (
            <div className="mb-12 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="blog-filters">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" size={16} />
                <input
                  type="text"
                  data-testid="blog-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search articles..."
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#17B9BE]/50 transition-colors"
                />
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2" data-testid="blog-category-filters">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    data-testid={`blog-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                    className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${
                      activeCategory === cat
                        ? 'bg-[#17B9BE] text-black'
                        : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-20">
              <div className="text-white/40">Loading posts...</div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-white/40">
                {posts.length === 0 ? 'No blog posts available yet.' : 'No articles match your search.'}
              </div>
              {searchQuery || activeCategory !== 'All' ? (
                <button
                  onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
                  className="mt-4 text-sm text-[#17B9BE] hover:text-white transition-colors"
                  data-testid="blog-clear-filters"
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <p className="text-xs text-white/30 mb-6">{filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    data-testid={`blog-post-${index}`}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      className="group block bg-[#0a0a0a] rounded overflow-hidden border border-white/5 hover:border-[#17B9BE]/20 transition-all duration-300 gradient-border"
                    >
                      <div className="relative h-52 overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/30 to-transparent" />
                        <div className="absolute bottom-4 left-4">
                          <span className="px-3 py-1 bg-[#17B9BE]/20 backdrop-blur-sm rounded text-xs font-semibold text-[#17B9BE] border border-[#17B9BE]/20">
                            {post.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center text-[10px] text-white/30 mb-4 space-x-4 uppercase tracking-wider">
                          <span className="flex items-center">
                            <FiCalendar className="mr-1" size={10} />
                            {formatDate(post.published_at)}
                          </span>
                          <span className="flex items-center">
                            <FiUser className="mr-1" size={10} />
                            {post.author}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold mb-3 group-hover:text-[#17B9BE] transition-colors leading-snug">
                          {post.title}
                        </h3>
                        <p className="text-white/35 text-sm mb-5 line-clamp-3 leading-relaxed">{post.excerpt}</p>
                        <div className="flex items-center text-white/40 group-hover:text-[#17B9BE] transition-colors">
                          <span className="text-xs uppercase tracking-wider font-medium">Read Article</span>
                          <FiArrowUpRight className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={14} />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
